import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';

const ProductDetaila = () => {
  const { productId } = useParams(); // Changed from 'id' to 'productId'
  
  console.log('🔄 Fetching product with ID:', productId); 
  const navigate = useNavigate();
  const location = useLocation();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 480);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsSmallScreen(window.innerWidth < 480);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchProduct();
  }, [productId]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const adminToken = localStorage.getItem('adminToken');
      
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (adminToken) {
        headers['Authorization'] = `Bearer ${adminToken}`;
      }
      
      const response = await fetch(`${API_URL}/admin/products/${productId}`, {
        headers: headers
      });
      
      if (response.status === 404) {
        toast.error('Product not found');
        navigate('/admin/products');
        return;
      }
      
      if (!response.ok) {
        throw new Error(`Server returned ${response.status}`);
      }
      
      const data = await response.json();
      setProduct(data.product || data);
      
    } catch (error) {
      console.error('❌ Error fetching product:', error);
      toast.error('Failed to load product details');
      navigate('/admin/products');
    } finally {
      setLoading(false);
    }
  };

  // Calculate available stock (stock minus sold items ONLY)
  const calculateAvailableStock = (product) => {
    return (product?.inventory?.stock || 0) - (product?.inventory?.soldStock || 0);
  };

  // Get stock breakdown
  const getStockBreakdown = (product) => {
    return {
      total: product?.inventory?.stock || 0,
      sold: product?.inventory?.soldStock || 0,
      reserved: product?.inventory?.reservedStock || 0,
      available: calculateAvailableStock(product)
    };
  };

  // Calculate stock status
  const getStockStatus = (product) => {
    const stockBreakdown = getStockBreakdown(product);
    const availableStock = stockBreakdown.available;
    const threshold = product?.inventory?.lowStockThreshold || 10;
    
    if (product?.productType === 'pre-order' || product?.productType === 'growing' || product?.stockStatus === 'pre-order') {
      return { status: 'pre-order', color: 'var(--info)', text: 'Pre-Order' };
    }
    
    if (availableStock <= 0) return { status: 'out-of-stock', color: 'var(--error)', text: 'Out of Stock' };
    if (availableStock <= threshold) return { status: 'low-stock', color: 'var(--warning)', text: 'Low Stock' };
    return { status: 'in-stock', color: 'var(--success)', text: 'In Stock' };
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Safe category name getter
  const getCategoryName = (product) => {
    if (!product?.category) return 'Uncategorized';
    if (typeof product.category === 'string') return product.category;
    if (typeof product.category === 'object') return product.category.name || 'Uncategorized';
    return 'Uncategorized';
  };

  // Safe description getter
  const getDescription = (product) => {
    if (!product?.description) return 'No description available';
    if (typeof product.description === 'string') return product.description;
    return 'No description available';
  };

  const handleEdit = () => {
    navigate(`/admin/products/edit/${productId}`);
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this product? This action cannot be undone.')) {
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        const adminToken = localStorage.getItem('adminToken');
        
        const response = await fetch(`${API_URL}/admin/products/${productId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${adminToken}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          toast.success('Product deleted successfully');
          navigate('/admin/products');
        } else {
          throw new Error('Failed to delete product');
        }
      } catch (error) {
        console.error('Error deleting product:', error);
        toast.error('Failed to delete product');
      }
    }
  };

  const handleBack = () => {
    if (location.state?.fromProductsPage) {
      navigate('/admin/products');
    } else {
      navigate(-1);
    }
  };

  if (loading) {
    return (
      <div className="admin-content" style={{ 
        padding: isSmallScreen ? 'var(--space-2)' : (isMobile ? 'var(--space-4)' : 'var(--space-6)'),
        maxWidth: '100%',
        overflowX: 'hidden'
      }}>
        <div style={{ 
          textAlign: 'center', 
          padding: 'var(--space-12)', 
          color: 'var(--text-light)'
        }}>
          <div style={{ marginBottom: 'var(--space-3)' }}>🔄</div>
          Loading product details...
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="admin-content" style={{ 
        padding: isSmallScreen ? 'var(--space-2)' : (isMobile ? 'var(--space-4)' : 'var(--space-6)'),
        maxWidth: '100%',
        overflowX: 'hidden'
      }}>
        <div style={{ 
          textAlign: 'center', 
          padding: 'var(--space-12)', 
          color: 'var(--text-light)'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: 'var(--space-3)', opacity: 0.3 }}>❌</div>
          <p style={{ 
            fontWeight: '600', 
            color: 'var(--text-dark)',
            fontSize: isSmallScreen ? 'var(--font-size-sm)' : 'var(--font-size-base)',
            marginBottom: 'var(--space-2)'
          }}>
            Product not found
          </p>
          <button 
            className="btn btn-gold"
            onClick={handleBack}
            style={{ 
              padding: isSmallScreen ? '8px 16px' : 'var(--space-3) var(--space-5)',
              fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
              marginTop: 'var(--space-4)'
            }}
          >
            ← Back to Products
          </button>
        </div>
      </div>
    );
  }

  const stockInfo = getStockStatus(product);
  const stockBreakdown = getStockBreakdown(product);
  const categoryName = getCategoryName(product);
  const description = getDescription(product);

  return (
    <div className="admin-content" style={{ 
      padding: isSmallScreen ? 'var(--space-2)' : (isMobile ? 'var(--space-4)' : 'var(--space-6)'),
      maxWidth: '100%',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <div style={{ 
        marginBottom: isSmallScreen ? 'var(--space-3)' : (isMobile ? 'var(--space-5)' : 'var(--space-6)'),
        paddingBottom: isSmallScreen ? 'var(--space-2)' : (isMobile ? 'var(--space-3)' : 'var(--space-4)'),
        borderBottom: '1px solid var(--border-light)'
      }}>
        <div style={{ 
          display: 'flex', 
          flexDirection: isSmallScreen ? 'column' : 'row',
          alignItems: isSmallScreen ? 'flex-start' : 'center',
          justifyContent: 'space-between',
          gap: isSmallScreen ? 'var(--space-2)' : 'var(--space-4)',
          marginBottom: isSmallScreen ? 'var(--space-2)' : 'var(--space-3)'
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 'var(--space-2)',
              marginBottom: 'var(--space-2)'
            }}>
              <button 
                onClick={handleBack}
                className="btn btn-secondary"
                style={{ 
                  padding: isSmallScreen ? '4px 8px' : 'var(--space-2) var(--space-3)',
                  fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                }}
              >
                ← Back
              </button>
              <h1 className="accent-gold" style={{ 
                fontSize: isSmallScreen ? 'var(--font-size-lg)' : (isMobile ? 'var(--font-size-xl)' : 'var(--font-size-2xl)'),
                margin: 0,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                {product.name || 'Unnamed Product'}
              </h1>
            </div>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: isSmallScreen ? '4px' : 'var(--space-2)'
            }}>
              <span style={{ 
                backgroundColor: stockInfo.color,
                color: 'white',
                padding: isSmallScreen ? '2px 6px' : '4px 8px',
                borderRadius: 'var(--radius-sm)',
                fontSize: isSmallScreen ? '10px' : 'var(--font-size-xs)',
                fontWeight: '600'
              }}>
                {stockInfo.text}
              </span>
              <span style={{ 
                backgroundColor: product.status === 'active' ? 'var(--success)' : 'var(--error)',
                color: 'white',
                padding: isSmallScreen ? '2px 6px' : '4px 8px',
                borderRadius: 'var(--radius-sm)',
                fontSize: isSmallScreen ? '10px' : 'var(--font-size-xs)',
                fontWeight: '600',
                textTransform: 'capitalize'
              }}>
                {product.status}
              </span>
              <span style={{ 
                backgroundColor: 'var(--bg-light)',
                color: 'var(--text-medium)',
                padding: isSmallScreen ? '2px 6px' : '4px 8px',
                borderRadius: 'var(--radius-sm)',
                fontSize: isSmallScreen ? '10px' : 'var(--font-size-xs)',
                fontWeight: '500'
              }}>
                {categoryName}
              </span>
              {product.sku && (
                <span style={{ 
                  backgroundColor: 'var(--bg-light)',
                  color: 'var(--text-medium)',
                  padding: isSmallScreen ? '2px 6px' : '4px 8px',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: isSmallScreen ? '10px' : 'var(--font-size-xs)',
                  fontFamily: 'monospace',
                  fontWeight: '500'
                }}>
                  SKU: {product.sku}
                </span>
              )}
            </div>
          </div>
          
          <div style={{ 
            display: 'flex', 
            gap: isSmallScreen ? '4px' : 'var(--space-2)',
            flexShrink: 0
          }}>
            
            <button 
              onClick={handleDelete}
              className="btn btn-danger"
              style={{ 
                padding: isSmallScreen ? '6px 10px' : (isMobile ? 'var(--space-2) var(--space-3)' : 'var(--space-3) var(--space-4)'),
                fontSize: isSmallScreen ? 'var(--font-size-xs)' : (isMobile ? 'var(--font-size-sm)' : 'var(--font-size-base)'),
                whiteSpace: 'nowrap'
              }}
            >
              Delete
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isSmallScreen ? '1fr' : (isMobile ? '1fr' : '1fr 1fr'),
        gap: isSmallScreen ? 'var(--space-2)' : (isMobile ? 'var(--space-4)' : 'var(--space-6)'),
        marginBottom: isSmallScreen ? 'var(--space-3)' : (isMobile ? 'var(--space-5)' : 'var(--space-6)')
      }}>
        {/* Left Column - Product Info */}
        <div className="card card-gold">
          <div className="card-header" style={{
            padding: isSmallScreen ? 'var(--space-2) var(--space-3)' : (isMobile ? 'var(--space-3) var(--space-4)' : 'var(--space-4) var(--space-5)')
          }}>
            <h3 style={{ 
              margin: 0,
              fontSize: isSmallScreen ? 'var(--font-size-sm)' : (isMobile ? 'var(--font-size-base)' : 'var(--font-size-lg)')
            }}>
              Product Information
            </h3>
          </div>
          
          <div className="card-body" style={{
            padding: isSmallScreen ? 'var(--space-3)' : (isMobile ? 'var(--space-4)' : 'var(--space-5)')
          }}>
            {/* Product Image */}
            <div style={{ 
              marginBottom: isSmallScreen ? 'var(--space-3)' : 'var(--space-4)',
              textAlign: 'center'
            }}>
              {product.featuredImage?.url ? (
                <img 
                  src={product.featuredImage.url} 
                  alt={product.featuredImage.alt || product.name}
                  style={{
                    width: '100%',
                    maxWidth: '300px',
                    height: 'auto',
                    borderRadius: 'var(--radius-lg)',
                    objectFit: 'cover',
                    margin: '0 auto'
                  }}
                />
              ) : (
                <div style={{
                  width: '100%',
                  maxWidth: '300px',
                  height: '200px',
                  borderRadius: 'var(--radius-lg)',
                  backgroundColor: 'var(--bg-gray)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--text-light)',
                  margin: '0 auto'
                }}>
                  🖼️ No Image
                </div>
              )}
            </div>
            
            {/* Basic Info */}
            <div style={{ 
              display: 'grid',
              gridTemplateColumns: isSmallScreen ? '1fr' : '1fr 1fr',
              gap: isSmallScreen ? 'var(--space-2)' : 'var(--space-3)',
              marginBottom: isSmallScreen ? 'var(--space-3)' : 'var(--space-4)'
            }}>
              <div>
                <label style={{ 
                  display: 'block', 
                  fontSize: 'var(--font-size-xs)',
                  marginBottom: '4px',
                  color: 'var(--text-light)',
                  fontWeight: '500'
                }}>
                  Price
                </label>
                <div style={{ 
                  fontSize: 'var(--font-size-xl)', 
                  fontWeight: '700', 
                  color: 'var(--accent-gold)'
                }}>
                  KSh {product.price?.toLocaleString() || '0'}
                </div>
              </div>
              
              <div>
                <label style={{ 
                  display: 'block', 
                  fontSize: 'var(--font-size-xs)',
                  marginBottom: '4px',
                  color: 'var(--text-light)',
                  fontWeight: '500'
                }}>
                  Product Type
                </label>
                <div style={{ 
                  fontSize: 'var(--font-size-base)', 
                  fontWeight: '600', 
                  color: 'var(--text-dark)'
                }}>
                  {product.productType || 'Standard'}
                </div>
              </div>
              
              <div>
                <label style={{ 
                  display: 'block', 
                  fontSize: 'var(--font-size-xs)',
                  marginBottom: '4px',
                  color: 'var(--text-light)',
                  fontWeight: '500'
                }}>
                  Created
                </label>
                <div style={{ 
                  fontSize: 'var(--font-size-sm)', 
                  color: 'var(--text-medium)'
                }}>
                  {formatDate(product.createdAt)}
                </div>
              </div>
              
              <div>
                <label style={{ 
                  display: 'block', 
                  fontSize: 'var(--font-size-xs)',
                  marginBottom: '4px',
                  color: 'var(--text-light)',
                  fontWeight: '500'
                }}>
                  Last Updated
                </label>
                <div style={{ 
                  fontSize: 'var(--font-size-sm)', 
                  color: 'var(--text-medium)'
                }}>
                  {formatDate(product.updatedAt)}
                </div>
              </div>
            </div>
            
            {/* Description */}
            <div style={{ marginTop: 'var(--space-4)' }}>
              <label style={{ 
                display: 'block', 
                fontSize: 'var(--font-size-sm)',
                marginBottom: 'var(--space-2)',
                color: 'var(--text-dark)',
                fontWeight: '600'
              }}>
                Description
              </label>
              <div style={{ 
                padding: 'var(--space-3)',
                backgroundColor: 'var(--bg-light)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                lineHeight: 1.6,
                color: 'var(--text-medium)',
                maxHeight: '200px',
                overflowY: 'auto'
              }}>
                {description}
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Stock & Inventory */}
        <div className="card card-gold">
          <div className="card-header" style={{
            padding: isSmallScreen ? 'var(--space-2) var(--space-3)' : (isMobile ? 'var(--space-3) var(--space-4)' : 'var(--space-4) var(--space-5)')
          }}>
            <h3 style={{ 
              margin: 0,
              fontSize: isSmallScreen ? 'var(--font-size-sm)' : (isMobile ? 'var(--font-size-base)' : 'var(--font-size-lg)')
            }}>
              Stock & Inventory
            </h3>
          </div>
          
          <div className="card-body" style={{
            padding: isSmallScreen ? 'var(--space-3)' : (isMobile ? 'var(--space-4)' : 'var(--space-5)')
          }}>
            {/* Stock Overview */}
            <div style={{ 
              display: 'grid',
              gridTemplateColumns: isSmallScreen ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
              gap: isSmallScreen ? '8px' : 'var(--space-3)',
              marginBottom: isSmallScreen ? 'var(--space-3)' : 'var(--space-4)'
            }}>
              {[
                { label: 'Total Stock', value: stockBreakdown.total, color: 'var(--primary)', icon: '📦' },
                { label: 'Available', value: stockBreakdown.available, color: stockBreakdown.available > 0 ? 'var(--success)' : 'var(--error)', icon: '✅' },
                { label: 'Sold', value: stockBreakdown.sold, color: 'var(--accent-gold)', icon: '💰' },
                { label: 'Reserved', value: stockBreakdown.reserved, color: 'var(--info)', icon: '⏳' }
              ].map((stat, index) => (
                <div key={index} style={{ 
                  textAlign: 'center',
                  padding: isSmallScreen ? '8px' : 'var(--space-3)',
                  backgroundColor: 'var(--bg-light)',
                  borderRadius: 'var(--radius-md)',
                  border: `1px solid ${stat.color}20`
                }}>
                  <div style={{ 
                    fontSize: '0.9rem',
                    color: stat.color,
                    marginBottom: '4px'
                  }}>
                    {stat.icon}
                  </div>
                  <div style={{ 
                    fontSize: isSmallScreen ? 'var(--font-size-lg)' : 'var(--font-size-xl)', 
                    fontWeight: '700', 
                    color: stat.color,
                    marginBottom: '2px'
                  }}>
                    {stat.value}
                  </div>
                  <div style={{ 
                    fontSize: isSmallScreen ? '9px' : 'var(--font-size-xs)', 
                    color: 'var(--text-medium)',
                    whiteSpace: 'nowrap'
                  }}>
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
            
            {/* Stock Settings */}
            <div style={{ 
              padding: isSmallScreen ? 'var(--space-2)' : 'var(--space-3)',
              backgroundColor: 'var(--bg-light)',
              borderRadius: 'var(--radius-md)',
              marginBottom: 'var(--space-3)'
            }}>
              <h4 style={{ 
                margin: '0 0 var(--space-2) 0',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--text-dark)'
              }}>
                Stock Settings
              </h4>
              <div style={{ 
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 'var(--space-2)',
                fontSize: 'var(--font-size-xs)'
              }}>
                <div>
                  <span style={{ color: 'var(--text-light)' }}>Low Stock Threshold:</span>
                  <div style={{ fontWeight: '600', color: 'var(--warning)' }}>
                    {product.inventory?.lowStockThreshold || 10}
                  </div>
                </div>
                <div>
                  
                </div>
              </div>
            </div>
            
            {/* Stock Status */}
            <div style={{ 
              padding: isSmallScreen ? 'var(--space-2)' : 'var(--space-3)',
              backgroundColor: 'var(--bg-light)',
              borderRadius: 'var(--radius-md)'
            }}>
              <h4 style={{ 
                margin: '0 0 var(--space-2) 0',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--text-dark)'
              }}>
                Stock Analysis
              </h4>
              
              {stockBreakdown.available <= 0 ? (
                <div style={{ 
                  padding: 'var(--space-2)',
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid var(--error)',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--error)',
                  fontSize: 'var(--font-size-sm)',
                  textAlign: 'center',
                  fontWeight: '600'
                }}>
                  Out of Stock - Needs restocking
                </div>
              ) : stockBreakdown.available <= (product.inventory?.lowStockThreshold || 10) ? (
                <div style={{ 
                  padding: 'var(--space-2)',
                  backgroundColor: 'rgba(245, 158, 11, 0.1)',
                  border: '1px solid var(--warning)',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--warning)',
                  fontSize: 'var(--font-size-sm)',
                  textAlign: 'center',
                  fontWeight: '600'
                }}>
                  Low Stock - Consider restocking soon
                </div>
              ) : (
                <div style={{ 
                  padding: 'var(--space-2)',
                  backgroundColor: 'rgba(34, 197, 94, 0.1)',
                  border: '1px solid var(--success)',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--success)',
                  fontSize: 'var(--font-size-sm)',
                  textAlign: 'center',
                  fontWeight: '600'
                }}>
                  Stock levels are good
                </div>
              )}
              
              <div style={{ 
                fontSize: 'var(--font-size-xs)', 
                color: 'var(--text-light)',
                marginTop: 'var(--space-2)',
                textAlign: 'center'
              }}>
                {stockBreakdown.available} units available for sale
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Images */}
      {product.images && product.images.length > 0 && (
        <div className="card card-gold" style={{ marginBottom: isSmallScreen ? 'var(--space-3)' : (isMobile ? 'var(--space-5)' : 'var(--space-6)') }}>
          <div className="card-header" style={{
            padding: isSmallScreen ? 'var(--space-2) var(--space-3)' : (isMobile ? 'var(--space-3) var(--space-4)' : 'var(--space-4) var(--space-5)')
          }}>
            <h3 style={{ 
              margin: 0,
              fontSize: isSmallScreen ? 'var(--font-size-sm)' : (isMobile ? 'var(--font-size-base)' : 'var(--font-size-lg)')
            }}>
              Additional Images ({product.images.length})
            </h3>
          </div>
          
          <div className="card-body" style={{
            padding: isSmallScreen ? 'var(--space-3)' : (isMobile ? 'var(--space-4)' : 'var(--space-5)')
          }}>
            <div style={{ 
              display: 'grid',
              gridTemplateColumns: isSmallScreen ? 'repeat(2, 1fr)' : (isMobile ? 'repeat(3, 1fr)' : 'repeat(4, 1fr)'),
              gap: isSmallScreen ? '8px' : 'var(--space-3)'
            }}>
              {product.images.map((image, index) => (
                <div key={index} style={{
                  aspectRatio: '1/1',
                  borderRadius: 'var(--radius-md)',
                  overflow: 'hidden',
                  border: '1px solid var(--border-light)'
                }}>
                  <img 
                    src={image.url} 
                    alt={image.alt || `${product.name} - Image ${index + 1}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center',
        gap: isSmallScreen ? 'var(--space-2)' : 'var(--space-4)',
        paddingTop: isSmallScreen ? 'var(--space-3)' : (isMobile ? 'var(--space-4)' : 'var(--space-5)'),
        borderTop: '1px solid var(--border-light)'
      }}>
        
        
        <button 
          onClick={() => navigate('/admin/products')}
          className="btn btn-secondary"
          style={{ 
            padding: isSmallScreen ? '8px 16px' : 'var(--space-3) var(--space-5)',
            fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
          }}
        >
          🛍️ View All Products
        </button>
      </div>
    </div>
  );
};

export default ProductDetaila;